/* Code samples shared by the public /developers page and the in-app Developers console.
   Keep in sync with docs/developer-api.md. */

export const API_BASE = 'https://gotrutax.com/api/v1/public';
export const MCP_URL = 'https://gotrutax.com/mcp';
export const FREE_MONTHLY_CALCS = 1000;
export const UPGRADE_EMAIL = 'support@gotrutax.com';
export const PIPX_INSTALL = 'pipx install "git+https://github.com/zohebk/gotrutax.git#subdirectory=mcp-server"';

export const KEY_PLACEHOLDER = 'gtt_test_YOUR_KEY';

export type SnippetLang = 'curl' | 'python' | 'node';

const CART = `{
    "line_items": [
      {"reference": "sku_tshirt", "unit_amount": 2500, "quantity": 2, "tax_code": "txcd_99999999"}
    ],
    "ship_to": {"line1": "1 Monument Cir", "city": "Indianapolis", "state": "IN", "postal_code": "46204"}
  }`;

export const calculateSnippet = (lang: SnippetLang, key: string = KEY_PLACEHOLDER): string => {
  switch (lang) {
    case 'curl':
      return `curl ${API_BASE}/calculate \\
  -H "Authorization: Bearer ${key}" \\
  -H "Idempotency-Key: order-1001" \\
  -H "Content-Type: application/json" \\
  -d '${CART}'`;
    case 'python':
      return `import requests

resp = requests.post(
    "${API_BASE}/calculate",
    headers={"Authorization": "Bearer ${key}", "Idempotency-Key": "order-1001"},
    json={
        "line_items": [
            {"reference": "sku_tshirt", "unit_amount": 2500, "quantity": 2, "tax_code": "txcd_99999999"},
        ],
        "ship_to": {"line1": "1 Monument Cir", "city": "Indianapolis", "state": "IN", "postal_code": "46204"},
    },
    timeout=30,
)
resp.raise_for_status()
calc = resp.json()
print(calc["tax_total"], "cents of sales tax;", resp.headers["X-Quota-Remaining"], "calls left this month")`;
    case 'node':
      return `const resp = await fetch("${API_BASE}/calculate", {
  method: "POST",
  headers: {
    "Authorization": "Bearer ${key}",
    "Idempotency-Key": "order-1001",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    line_items: [{ reference: "sku_tshirt", unit_amount: 2500, quantity: 2, tax_code: "txcd_99999999" }],
    ship_to: { line1: "1 Monument Cir", city: "Indianapolis", state: "IN", postal_code: "46204" },
  }),
});
if (!resp.ok) throw new Error((await resp.json()).error.message);
const calc = await resp.json();
console.log(calc.tax_total, "cents of sales tax");`;
    default:
      return '';
  }
};

export const mcpHostedConfig = (key: string = KEY_PLACEHOLDER): string =>
  JSON.stringify({ mcpServers: { gotrutax: { url: MCP_URL, headers: { Authorization: `Bearer ${key}` } } } }, null, 2);

const EXEMPT_CART = `{
    "line_items": [
      {"reference": "sku_bulk_flour", "unit_amount": 12000, "quantity": 4, "tax_code": "txcd_99999999"}
    ],
    "ship_to": {"line1": "1 Monument Cir", "city": "Indianapolis", "state": "IN", "postal_code": "46204"},
    "customer_id": 4821
  }`;

/* Checkout with a stored exemption certificate: send the customer id and let GoTruTax
   apply an approved, unexpired certificate for the ship-to state. */
export const exemptionSnippet = (lang: SnippetLang, key: string = KEY_PLACEHOLDER): string => {
  switch (lang) {
    case 'curl':
      return `curl ${API_BASE}/calculate \\
  -H "Authorization: Bearer ${key}" \\
  -H "Idempotency-Key: order-1002" \\
  -H "Content-Type: application/json" \\
  -d '${EXEMPT_CART}'`;
    case 'python':
      return `import requests

resp = requests.post(
    "${API_BASE}/calculate",
    headers={"Authorization": "Bearer ${key}", "Idempotency-Key": "order-1002"},
    json={
        "line_items": [
            {"reference": "sku_bulk_flour", "unit_amount": 12000, "quantity": 4, "tax_code": "txcd_99999999"},
        ],
        "ship_to": {"line1": "1 Monument Cir", "city": "Indianapolis", "state": "IN", "postal_code": "46204"},
        "customer_id": 4821,
    },
    timeout=30,
)
resp.raise_for_status()
calc = resp.json()
if calc["exemption"]["applied"]:
    print("certificate", calc["exemption"]["certificate_id"], "exempted", calc["exempt_amount"], "cents")
else:
    print("no certificate on file; tax due:", calc["tax_total"])`;
    case 'node':
      return `const resp = await fetch("${API_BASE}/calculate", {
  method: "POST",
  headers: {
    "Authorization": "Bearer ${key}",
    "Idempotency-Key": "order-1002",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    line_items: [{ reference: "sku_bulk_flour", unit_amount: 12000, quantity: 4, tax_code: "txcd_99999999" }],
    ship_to: { line1: "1 Monument Cir", city: "Indianapolis", state: "IN", postal_code: "46204" },
    customer_id: 4821,
  }),
});
if (!resp.ok) throw new Error((await resp.json()).error.message);
const calc = await resp.json();
console.log(calc.exemption.applied ? \`exempt: \${calc.exempt_amount} cents\` : \`tax: \${calc.tax_total} cents\`);`;
    default:
      return '';
  }
};

/* Check a certificate before you rely on it. Free; never returns the document. */
export const exemptionValidateSnippet = (lang: SnippetLang): string => {
  switch (lang) {
    case 'curl':
      return `curl "${API_BASE}/exemption-certificates/918/validate?state=IN&on_date=2026-11-04" \\
  -H "Authorization: Bearer ${KEY_PLACEHOLDER}"`;
    case 'python':
      return `import requests

resp = requests.get(
    "${API_BASE}/exemption-certificates/918/validate",
    headers={"Authorization": "Bearer ${KEY_PLACEHOLDER}"},
    params={"state": "IN", "on_date": "2026-11-04"},
    timeout=30,
)
resp.raise_for_status()
print(resp.json())  # valid_for_state, expiry_status (valid | expiring_soon | expired), exempt_reason`;
    case 'node':
      return `const url = new URL("${API_BASE}/exemption-certificates/918/validate");
url.searchParams.set("state", "IN");
url.searchParams.set("on_date", "2026-11-04");
const resp = await fetch(url, { headers: { Authorization: "Bearer ${KEY_PLACEHOLDER}" } });
const status = await resp.json();
console.log(status.valid_for_state, status.expiry_status);`;
    default:
      return '';
  }
};

export const EXEMPTION_RESPONSE = `{
  "object": "tax_calculation",
  "subtotal": 48000,
  "tax_total": 0,
  "total": 48000,
  "effective_rate": 0,
  "exempt_amount": 3360,
  "exemption": {
    "applied": true,
    "certificate_id": 918,
    "customer_id": 4821,
    "state": "IN",
    "form_type": "sst",
    "exempt_reason": "resale",
    "expiry_date": "2027-03-31",
    "expiry_status": "valid"
  },
  "zero_tax_reason": {"code": "customer_exempt", "message": "The customer is exempt from sales tax."}
}`;

export const ERROR_CODES: { status: string; codes: string; meaning: string }[] = [
  { status: '400', codes: 'invalid_idempotency_key, invalid_quote, quote_expired', meaning: 'The request itself is malformed. Fix it and retry.' },
  { status: '401', codes: 'missing_api_key, invalid_api_key, api_key_revoked', meaning: 'No valid key. Send Authorization: Bearer gtt_live_…. Create a new key if it was revoked.' },
  { status: '402', codes: 'quota_exceeded', meaning: 'Free monthly calls are used up and the account has no paid plan. The message links to the upgrade path.' },
  { status: '403', codes: 'account_disabled, company_required', meaning: "The key's account is disabled, or customer_id was sent with a key that is not tied to a company." },
  { status: '404', codes: 'customer_not_found, certificate_not_found, not_found', meaning: "The customer, certificate or transaction does not exist in this key's company." },
  { status: '409', codes: 'idempotency_key_reused, idempotency_request_in_progress, already_committed', meaning: 'The same Idempotency-Key was used with a different body, or a quote was already committed.' },
  { status: '422', codes: 'validation_failed, invalid_postal_code, invalid_state, address_incomplete, too_many_line_items, engine_validation_failed', meaning: 'The cart or address failed validation. details[] names each field.' },
  { status: '429', codes: 'rate_limited, test_quota_exceeded', meaning: 'Slow down (Retry-After), or a test key reached its monthly cap.' },
  { status: '502', codes: 'engine_error', meaning: 'The tax engine failed. The call is not billed and is safe to retry.' },
];

export const mcpStdioConfig = (key: string = KEY_PLACEHOLDER): string =>
  JSON.stringify({ mcpServers: { gotrutax: { command: 'gotrutax-mcp', env: { GOTRUTAX_API_KEY: key } } } }, null, 2);

export const claudeCodeCommand = (key: string = KEY_PLACEHOLDER): string =>
  `claude mcp add --transport http gotrutax ${MCP_URL} --header "Authorization: Bearer ${key}"`;

export const upgradeMailto = (account?: string): string => {
  const subject = 'Upgrade to paid API usage';
  const body = `Hi GoTruTax team,\n\nI'd like to continue using the developer API past the free ${FREE_MONTHLY_CALCS.toLocaleString('en-US')} calculations per month.${
    account ? `\n\nAccount: ${account}` : ''
  }\nExpected monthly calculations: \n\nThanks`;
  return `mailto:${UPGRADE_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

export const ENDPOINTS: { method: string; path: string; billable: boolean; summary: string }[] = [
  { method: 'POST', path: '/calculate', billable: true, summary: 'Sales tax for a cart shipped to a US address, per line and per jurisdiction. Send customer_id to apply a stored exemption certificate. Accepts an Idempotency-Key.' },
  { method: 'GET', path: '/rates?zip=46204', billable: true, summary: 'Combined and per-jurisdiction rate for general goods at a ZIP code or state.' },
  { method: 'GET', path: '/exemption-certificates/918/validate?state=IN', billable: false, summary: 'Check a stored certificate for a state and date: valid, expiring in 30 days, or expired. Never returns the document.' },
  { method: 'POST', path: '/advisor/ask', billable: true, summary: 'Plain-English sales tax question answered from the engine, with sources and an as-of date.' },
  { method: 'POST', path: '/nexus/check', billable: false, summary: 'Economic nexus status in each state from your sales and transaction totals.' },
  { method: 'GET', path: '/usage', billable: false, summary: 'Calls used this month and the free calculations remaining.' },
  { method: 'POST', path: '/transactions', billable: false, summary: 'Commit a signed quote when the order is placed (quote-then-commit). Idempotent per quote.' },
  { method: 'POST', path: '/transactions/{id}/reverse', billable: false, summary: 'Reverse a committed transaction after a refund or cancelled order.' },
];

export const MCP_TOOLS: { name: string; summary: string }[] = [
  { name: 'calculate_sales_tax', summary: 'Tax for a cart; same request as POST /calculate, including customer_id exemption.' },
  { name: 'get_tax_rates', summary: 'Rates for a ZIP code or state.' },
  { name: 'check_nexus', summary: 'Which states your sales have crossed.' },
  { name: 'ask_tax_advisor', summary: 'Plain-English sales tax questions, answered from the engine.' },
  { name: 'get_exemption_certificate_status', summary: 'Whether a stored certificate is valid for a state and date.' },
  { name: 'get_usage', summary: 'Calls used and remaining this month.' },
];

/* Real response from the GoTruTax engine for the sample cart (test key), abridged. */
export const SAMPLE_RESPONSE = `{
  "object": "tax_calculation",
  "livemode": false,
  "engine": "gotrutax",
  "subtotal": 5000,
  "tax_total": 350,
  "total": 5350,
  "line_items": [{
    "reference": "sku_tshirt",
    "amount": 5000,
    "tax_amount": 350,
    "tax_rate": 0.07,
    "explanation": "Taxable at the IN state rate of 7%."
  }],
  "jurisdictions": [{
    "level": "state",
    "state": "IN",
    "rate": 0.07,
    "tax_amount": 350
  }]
}`;
